// src/components/BookingCalendar.tsx

import React, { useState } from 'react';
import {
  addDays,
  addWeeks,
  isSameDay,
  setHours,
  setMinutes,
  isWithinInterval,
  addMinutes,
  format,
  differenceInMinutes,
  setSeconds,
  setMilliseconds
} from 'date-fns';
import { de } from 'date-fns/locale';
import { X, ChevronLeft, ChevronRight, Clock, CheckCircle } from 'lucide-react';
import { BlockedTimeModal } from './BlockedTimeModal';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { useSwipeable } from 'react-swipeable';
import { motion, AnimatePresence } from 'framer-motion';

interface BlockedTime {
  start: Date | { toDate: () => Date };
  end: Date | { toDate: () => Date };
  reason: string;
  recurring?: boolean;
  recurrenceEnd?: Date | { toDate: () => Date };
}

interface Booking {
  id: string;
  horseId: string;
  userId: string;
  userDisplayName: string;
  status: 'confirmed' | 'pending' | 'waitlist' | 'pending-confirmation';
  startTime: Date;
  endTime: Date;
  horseName?: string;
  createdAt?: Date;
  confirmationDeadline?: Date;
}

interface WaitlistEntry {
  id: string;
  facilityId: string;
  horseId: string;
  userId: string;
  startTime: Date;
  endTime: Date;
  createdAt: Date;
  status: string;
  userDisplayName: string;
  horseName?: string;
}

interface BookingCalendarProps {
  selectedDate: Date;
  onDateChange: (date: Date) => void;
  facility: {
    openingHours: Array<{ closed: boolean; start: string; end: string }>;
    capacity: number;
    bookingSettings: {
      defaultDuration: number;
      minBookingNotice: number;
      maxAdvanceBooking: number;
      autoConfirm?: boolean; // Falls verwendet
    };
    blockedTimes?: BlockedTime[];
  };
  existingBookings: Booking[];
  waitlistEntries: WaitlistEntry[]; 
  onTimeSlotSelect: (slot: { start: Date; end: Date } | null) => void;
  selectedTimeSlot: { start: Date; end: Date } | null;
  view: 'day' | 'week';
  onCancelBooking: (booking: Booking) => void;
  onConfirmBooking: (booking: Booking) => Promise<void>;
  isManager: boolean;
  canCancelBooking: (booking: Booking) => boolean;
}

export const BookingCalendar: React.FC<BookingCalendarProps> = ({
  selectedDate,
  onDateChange,
  facility,
  existingBookings,
  waitlistEntries,
  onTimeSlotSelect,
  selectedTimeSlot,
  view,
  onCancelBooking,
  onConfirmBooking,
  isManager,
  canCancelBooking,
}) => {
  const [blockedTimeModalOpen, setBlockedTimeModalOpen] = useState(false);
  const [selectedBlockedTime, setSelectedBlockedTime] = useState<BlockedTime | null>(null);

  // Hilfsfunktion zum Vergleichen von Slots bis zur Minute
  const isSameSlot = (date1: Date, date2: Date) => {
    return (
      date1.getFullYear() === date2.getFullYear() &&
      date1.getMonth() === date2.getMonth() &&
      date1.getDate() === date2.getDate() &&
      date1.getHours() === date2.getHours() &&
      date1.getMinutes() === date2.getMinutes()
    );
  };

  // Funktion zum Abrufen der Öffnungs- und Schließzeiten für ein bestimmtes Datum
  const getOpeningAndClosingTimes = (date: Date) => {
    const dayOfWeek = (date.getDay() + 6) % 7; 
    const dayHours = facility.openingHours[dayOfWeek];
    if (dayHours.closed) return null;

    const [openHourStr, openMinuteStr = '0'] = dayHours.start.split(':');
    const [closeHourStr, closeMinuteStr = '0'] = dayHours.end.split(':');

    return {
      openHour: parseInt(openHourStr, 10),
      openMinute: parseInt(openMinuteStr, 10),
      closeHour: parseInt(closeHourStr, 10),
      closeMinute: parseInt(closeMinuteStr, 10),
    };
  };

  const getEarliestOpeningTime = (dates: Date[]) => {
    let earliestTimeInMinutes = Infinity;

    dates.forEach((date) => {
      const times = getOpeningAndClosingTimes(date);
      if (times) {
        const openTimeInMinutes = times.openHour * 60 + times.openMinute;
        if (openTimeInMinutes < earliestTimeInMinutes) {
          earliestTimeInMinutes = openTimeInMinutes;
        }
      }
    });

    if (earliestTimeInMinutes === Infinity) {
      return null;
    }

    return {
      hour: Math.floor(earliestTimeInMinutes / 60),
      minute: earliestTimeInMinutes % 60,
    };
  };

  const getLatestClosingTime = (dates: Date[]) => {
    let latestTimeInMinutes = -Infinity;

    dates.forEach((date) => {
      const times = getOpeningAndClosingTimes(date);
      if (times) {
        const closeTimeInMinutes = times.closeHour * 60 + times.closeMinute;
        if (closeTimeInMinutes > latestTimeInMinutes) {
          latestTimeInMinutes = closeTimeInMinutes;
        }
      }
    });

    if (latestTimeInMinutes === -Infinity) {
      return null;
    }

    return {
      hour: Math.floor(latestTimeInMinutes / 60),
      minute: latestTimeInMinutes % 60,
    };
  };

  const getTimeSlots = (
    startHour: number,
    startMinute: number,
    endHour: number,
    endMinute: number
  ) => {
    const slots: { hour: number; minute: number }[] = [];
    const duration = facility.bookingSettings.defaultDuration;
    let currentMinutes = startHour * 60 + startMinute;
    const endMinutes = endHour * 60 + endMinute;

    while (currentMinutes < endMinutes) {
      const hour = Math.floor(currentMinutes / 60);
      const minute = currentMinutes % 60;
      slots.push({ hour, minute });
      currentMinutes += duration;
    }

    return slots;
  };

  const isSlotWithinOpeningHours = (date: Date, hour: number, minute: number = 0) => {
    const dayOfWeek = (date.getDay() + 6) % 7;
    const dayHours = facility.openingHours[dayOfWeek];
    if (dayHours.closed) return false;

    const [openHour, openMinute = '0'] = dayHours.start.split(':').map(Number);
    const [closeHour, closeMinute = '0'] = dayHours.end.split(':').map(Number);

    const slotTime = hour * 60 + minute;
    const openTime = openHour * 60 + Number(openMinute);
    const closeTime = closeHour * 60 + Number(closeMinute);

    return slotTime >= openTime && slotTime < closeTime;
  };

  const getBlockedTime = (date: Date, hour: number, minute: number = 0) => {
    const slotStart = setMinutes(setHours(date, hour), minute);
    const slotEnd = addMinutes(slotStart, facility.bookingSettings.defaultDuration);

    return facility.blockedTimes?.find((block) => {
      const blockStart = block.start instanceof Date ? block.start : block.start.toDate();
      const blockEnd = block.end instanceof Date ? block.end : block.end.toDate();
      return (
        isWithinInterval(slotStart, { start: blockStart, end: blockEnd }) ||
        isWithinInterval(slotEnd, { start: blockStart, end: blockEnd }) ||
        (blockStart <= slotStart && blockEnd >= slotEnd)
      );
    });
  };

  const isSlotBookable = (slotStart: Date) => {
    const now = new Date();
    const noticeMinutes = differenceInMinutes(slotStart, now);
    const advanceMinutes = differenceInMinutes(slotStart, now);
    const advanceDays = advanceMinutes / (24 * 60);

    return (
      noticeMinutes >= facility.bookingSettings.minBookingNotice &&
      advanceDays <= facility.bookingSettings.maxAdvanceBooking
    );
  };

  const getBookingRestrictionMessage = (slotStart: Date) => {
    const now = new Date();
    const noticeMinutes = differenceInMinutes(slotStart, now);
    const advanceMinutes = differenceInMinutes(slotStart, now);
    const advanceDays = advanceMinutes / (24 * 60);

    if (noticeMinutes < facility.bookingSettings.minBookingNotice) {
      return `Mindestens ${facility.bookingSettings.minBookingNotice} Min. Vorlauf`;
    }

    if (advanceDays > facility.bookingSettings.maxAdvanceBooking) {
      return `Max. ${facility.bookingSettings.maxAdvanceBooking} Tage im Voraus`;
    }

    return '';
  };

  const getBookingsForSlot = (date: Date, hour: number, minute: number = 0) => {
    const slotStart = setMinutes(setHours(date, hour), minute);
    return existingBookings.filter((booking) => {
      return isSameSlot(booking.startTime, slotStart);
    });
  };

  const getWaitlistEntriesForSlot = (date: Date, hour: number, minute: number = 0) => {
    const slotStart = setMinutes(setHours(date, hour), minute);
    return waitlistEntries.filter((entry) => {
      return isSameSlot(entry.startTime, slotStart);
    });
  };

  const hasWaitlist = (date: Date, hour: number, minute: number = 0) => {
    const entries = getWaitlistEntriesForSlot(date, hour, minute);
    return entries.length > 0;
  };

  const getSlotBackgroundColor = (date: Date, hour: number, minute: number = 0) => {
    const isWithinHours = isSlotWithinOpeningHours(date, hour, minute);
    const isBlocked = getBlockedTime(date, hour, minute);
    const canBook = isSlotBookable(setMinutes(setHours(date, hour), minute));
    const bookings = getBookingsForSlot(date, hour, minute);
    const waitlists = getWaitlistEntriesForSlot(date, hour, minute);

    if (!isWithinHours) {
      return 'bg-gray-100 dark:bg-gray-700';
    }

    if (isBlocked) {
      return 'bg-pink-100 hover:bg-pink-200 dark:bg-pink-900 dark:hover:bg-pink-800 cursor-pointer';
    }

    if (!canBook) {
      return 'bg-gray-100 dark:bg-gray-700';
    }

    if (bookings.length >= facility.capacity) {
      return 'bg-red-100 hover:bg-red-200 dark:bg-red-900 dark:hover:bg-red-800';
    }

    if (waitlists.length > 0) {
      return 'bg-yellow-200 hover:bg-yellow-300 dark:bg-yellow-800 dark:hover:bg-yellow-700';
    }

    if (bookings.length === 0) {
      return 'bg-green-100 hover:bg-green-200 dark:bg-green-900 dark:hover:bg-green-800';
    }

    return 'bg-yellow-100 hover:bg-yellow-200 dark:bg-yellow-900 dark:hover:bg-yellow-800';
  };

  const getSlotStatus = (date: Date, hour: number, minute: number = 0) => {
    if (!isSlotWithinOpeningHours(date, hour, minute)) return 'Geschlossen';

    const blockedTime = getBlockedTime(date, hour, minute);
    if (blockedTime) return `Gesperrt (${blockedTime.reason})`;

    const canBook = isSlotBookable(setMinutes(setHours(date, hour), minute));
    if (!canBook) return getBookingRestrictionMessage(setMinutes(setHours(date, hour), minute));

    const bookings = getBookingsForSlot(date, hour, minute);
    const waitlists = getWaitlistEntriesForSlot(date, hour, minute);

    if (bookings.length >= facility.capacity) {
      return `${bookings.length}/${facility.capacity} belegt`;
    }

    if (waitlists.length > 0) {
      return `${waitlists.length} Wartelisten-Eintrag(e)`;
    }

    return 'Verfügbar';
  };

  const renderWaitlistIndicator = (date: Date, hour: number, minute: number = 0) => {
    const waitlists = getWaitlistEntriesForSlot(date, hour, minute);
    if (waitlists.length > 0) {
      return (
        <span className="absolute top-1 right-1 text-yellow-600 dark:text-yellow-400">
          <Clock className="h-4 w-4" />
        </span>
      );
    }
    return null;
  };

  const renderBookingStatus = (booking: Booking) => {
    if (booking.status === 'pending') {
      return (
        <div className="flex items-center">
          <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200">
            <Clock className="h-3 w-3 mr-1" />
            Ausstehend
          </span>
          {isManager && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onConfirmBooking(booking);
              }}
              className="ml-1 p-0.5 rounded-full text-green-600 hover:text-green-700 dark:text-green-400 dark:hover:text-green-300 hover:bg-green-100 dark:hover:bg-green-900/50"
              title="Buchung bestätigen"
            >
              <CheckCircle className="h-4 w-4" />
            </button>
          )}
        </div>
      );
    }
    if (booking.status === 'pending-confirmation') {
      return (
        <div className="flex items-center">
          <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
            <Clock className="h-3 w-3 mr-1" />
            Bestätigung ausstehend
          </span>
        </div>
      );
    }
    if (booking.status === 'confirmed') {
      return (
        <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
          Bestätigt
        </span>
      );
    }
    if (booking.status === 'waitlist') {
      return (
        <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200">
          Warteliste
        </span>
      );
    }
    return null;
  };

const renderTimeSlot = (date: Date, hour: number, minute: number = 0) => {
  const slotStart = setMinutes(setHours(date, hour), minute);
  const slotEnd = addMinutes(slotStart, facility.bookingSettings.defaultDuration);
  const bookings = getBookingsForSlot(date, hour, minute);
  const waitlists = getWaitlistEntriesForSlot(date, hour, minute);
  const blockedTime = getBlockedTime(date, hour, minute);
  const isSelected = selectedTimeSlot && isSameSlot(selectedTimeSlot.start, slotStart);

  const slotClasses = `
    relative border-b border-gray-200 dark:border-gray-700 
    ${view === 'week' ? 'h-16' : 'min-h-[5rem]'}
    ${getSlotBackgroundColor(date, hour, minute)}
    ${isSelected ? 'ring-2 ring-indigo-500 dark:ring-indigo-400' : ''}
    cursor-pointer
    transition-colors duration-200
  `;

  const handleSlotClick = () => {
    if (blockedTime) {
      setSelectedBlockedTime(blockedTime);
      setBlockedTimeModalOpen(true);
      return;
    }
    const adjustedStart = setSeconds(setMilliseconds(slotStart, 0), 0);
    const adjustedEnd = setSeconds(setMilliseconds(slotEnd, 0), 0);
    onTimeSlotSelect({ start: adjustedStart, end: adjustedEnd });
  };

  // Hilfsvariable, um "Verfügbar", "1 Wartelisten-Eintrag" etc. zu bekommen
  const slotStatus = getSlotStatus(date, hour, minute);

  return (
    <div
      key={`${date.toISOString()}-${hour}-${minute}`}
      className={slotClasses}
      onClick={handleSlotClick}
    >
      {view === 'week' ? (
        // Wochen-Ansicht
        <div className="absolute inset-0 flex items-center justify-center p-1">
          <div className="text-center text-sm">{slotStatus}</div>
          {renderWaitlistIndicator(date, hour, minute)}
        </div>
      ) : (
        // Tages-Ansicht
        <div className="h-full flex flex-col p-2">
          {/* 
            Bedingte Ausgabe:
            - Keine Bookings + Keine Warteliste -> zentriert
            - Sonst Status-Zeile und darunter Buchungen/Warteliste 
          */}
          {bookings.length === 0 && waitlists.length === 0 ? (
            // Keine Buchungen, keine Wartelisten -> Text in der Mitte zentrieren
            <div className="flex flex-col items-center justify-center h-full">
              <div className="text-center">{slotStatus}</div>
              {renderWaitlistIndicator(date, hour, minute)}
            </div>
          ) : (
            <>
              {/* Oben kurz den Status (z.B. "2 Wartelisten-Einträge") anzeigen */}
              <div className="text-center mb-2">{slotStatus}</div>

              {/* Bookings und Warteliste */}
              <div className="space-y-1 flex-1 relative">
                {/* Buchungen */}
                {bookings.map((booking) => (
                  <div
                    key={booking.id}
                    className="flex items-center justify-between bg-white/50 dark:bg-gray-800/50 rounded px-2 py-1"
                  >
                    <div className="flex flex-col min-w-0 flex-1">
                      <div className="flex items-center justify-center space-x-1">
                        <span className="font-medium text-sm truncate dark:text-gray-200">
                          {booking.horseName}
                        </span>
                        {renderBookingStatus(booking)}
                      </div>
                      <span className="text-xs text-center text-gray-600 dark:text-gray-400 truncate">
                        {booking.userDisplayName}
                      </span>
                    </div>
                    {canCancelBooking(booking) && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onCancelBooking(booking);
                        }}
                        className="ml-2 p-1.5 rounded-full hover:bg-red-100 dark:hover:bg-red-900/50 transition-colors hidden"
                        title="Buchung stornieren"
                      >
                        <X className="h-4 w-4 text-red-600 dark:text-red-400" />
                      </button>
                    )}
                  </div>
                ))}

                {/* Warteliste-Einträge (gelb hinterlegt) */}
                {waitlists.map((waitlistEntry) => (
                  <div
                    key={waitlistEntry.id}
                    className="flex items-center justify-between
                               bg-yellow-50 dark:bg-yellow-900/50
                               rounded px-2 py-1"
                  >
                    <div className="flex flex-col min-w-0 flex-1">
                      <div className="flex items-center justify-center space-x-1">
                        <span className="font-medium text-sm truncate dark:text-gray-200">
                          {waitlistEntry.horseName}
                        </span>
                        <span className="inline-flex items-center px-1.5 py-0.5 
                                         rounded-full text-xs font-medium
                                         bg-yellow-100 text-yellow-800 
                                         dark:bg-yellow-900 dark:text-yellow-200"
                        >
                          Warteliste
                        </span>
                      </div>
                      <span className="text-xs text-center text-gray-600 dark:text-gray-400 truncate">
                        {waitlistEntry.userDisplayName}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};


  // Tagesansicht
  const renderDayView = () => {
    const times = getOpeningAndClosingTimes(selectedDate);
    if (!times) {
      return (
        <div className="text-center py-8">
          <span className="text-lg font-medium">Geschlossen</span>
        </div>
      );
    }

    const timeSlots = getTimeSlots(
      times.openHour,
      times.openMinute,
      times.closeHour,
      times.closeMinute
    );

    return (
      <div className="flex flex-col divide-y divide-gray-200 dark:divide-gray-700">
        {timeSlots.map(({ hour, minute }) => (
          <div
            key={`slot-${hour}-${minute}`}
            className="flex items-center border-b border-gray-200 dark:border-gray-700 relative"
          >
            {/* Zeitlabel */}
            <div className="w-16 flex-shrink-0 flex items-center justify-center text-gray-500 dark:text-gray-400 text-xs font-medium">
              {format(setMinutes(setHours(selectedDate, hour), minute), 'HH:mm')}
            </div>
            {/* Slot-Inhalt */}
            <div className="flex-1">
              {renderTimeSlot(selectedDate, hour, minute)}
            </div>
          </div>
        ))}
      </div>
    );
  };

  // Wochenansicht
  const renderWeekView = () => {
    const days = Array.from({ length: 7 }, (_, i) => addDays(selectedDate, i));

    const earliestOpeningTime = getEarliestOpeningTime(days);
    const latestClosingTime = getLatestClosingTime(days);

    if (!earliestOpeningTime || !latestClosingTime) {
      return (
        <div className="text-center py-8">
          <span className="text-lg font-medium">Geschlossen</span>
        </div>
      );
    }

    const timeSlots = getTimeSlots(
      earliestOpeningTime.hour,
      earliestOpeningTime.minute,
      latestClosingTime.hour,
      latestClosingTime.minute
    );

    return (
      <div className="grid grid-cols-8 divide-x divide-gray-200 dark:divide-gray-700">
        {/* Zeitspalte */}
        <div className="flex flex-col">
          <div className="h-12 flex items-center justify-center font-medium text-gray-500 dark:text-gray-400 text-xs border-b border-gray-200 dark:border-gray-700">
            Uhr
          </div>
          {timeSlots.map(({ hour, minute }) => (
            <div
              key={`time-${hour}-${minute}`}
              className="flex items-center justify-center text-gray-500 dark:text-gray-400 text-xs border-b border-gray-200 dark:border-gray-700"
              style={{ height: '4rem' }}
            >
              {format(setMinutes(setHours(new Date(), hour), minute), 'HH:mm')}
            </div>
          ))}
        </div>
        {/* Wochentage */}
        {days.map((day) => (
          <div key={day.toISOString()} className="flex flex-col">
            <div className="h-12 flex items-center justify-center border-b border-gray-200 dark:border-gray-700">
              <div className="text-center">
                <div className="font-medium text-gray-900 dark:text-white text-sm">
                  {format(day, 'EEE', { locale: de })}
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400">
                  {format(day, 'dd.MM.')}
                </div>
              </div>
            </div>
            {timeSlots.map(({ hour, minute }) => renderTimeSlot(day, hour, minute))}
          </div>
        ))}
      </div>
    );
  };

  // Swipe-Gesten
  const handlePrevious = () => {
    onDateChange(view === 'week' ? addWeeks(selectedDate, -1) : addDays(selectedDate, -1));
  };

  const handleNext = () => {
    onDateChange(view === 'week' ? addWeeks(selectedDate, 1) : addDays(selectedDate, 1));
  };

  const handlers = useSwipeable({
    onSwipedLeft: () => handleNext(),
    onSwipedRight: () => handlePrevious(),
    preventDefaultTouchmoveEvent: true,
    trackMouse: true,
  });

  return (
    <div className="w-full relative" {...handlers}>
      {/* Kopfbereich mit Buttons */}
      <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center space-x-2">
          <button
            onClick={handlePrevious}
            className="p-3 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-300"
            title={view === 'week' ? 'Vorherige Woche' : 'Vorheriger Tag'}
          >
            <ChevronLeft className="h-6 w-6" />
          </button>
          <button
            onClick={handleNext}
            className="p-3 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-300"
            title={view === 'week' ? 'Nächste Woche' : 'Nächster Tag'}
          >
            <ChevronRight className="h-6 w-6" />
          </button>
        </div>
        {/* DatePicker */}
        <div className="flex-1 flex justify-center">
          <DatePicker
            selected={selectedDate}
            onChange={(date: Date) => onDateChange(date)}
            dateFormat="EEEE, dd.MM.yyyy"
            locale={de}
            className="w-full sm:w-auto p-2 border rounded-md dark:bg-gray-700 dark:text-white"
            placeholderText="Datum auswählen"
          />
        </div>
      </div>

      {/* Kalenderansicht mit animierten Übergängen */}
      <AnimatePresence mode="wait">
        <motion.div
          key={`${view}-${selectedDate.toDateString()}`}
          initial={{ opacity: 0, x: 100 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -100 }}
          transition={{ duration: 0.3 }}
          className="w-full overflow-x-auto"
        >
          {view === 'week' ? renderWeekView() : renderDayView()}
        </motion.div>
      </AnimatePresence>

      {/* Floating Action Button (optional) */}
      <button
        className="fixed bottom-4 right-4 bg-indigo-600 text-white p-4 rounded-full shadow-lg hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 block md:hidden"
        onClick={() => onDateChange(new Date())}
        title="Heute"
      >
        Heute
      </button>

      {/* Modal für gesperrte Zeiten */}
      <BlockedTimeModal
        isOpen={blockedTimeModalOpen}
        onClose={() => setBlockedTimeModalOpen(false)}
        blockedTime={selectedBlockedTime}
      />
    </div>
  );
};
